import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import { useCart } from '../../context/CartContext';
import { useNotifications } from '../../context/NotificationContext';
import './ItemsDetallePage.css';
import { logError } from '../../utils/logger';
import { formatARS } from '../../utils/currency';
import { computeItemUnitPrice } from '../../utils/itemPricing';
import { useStoreSettings } from '../../hooks/useStoreSettings';
import ShippingNotice from '../../components/ShippingNotice/ShippingNotice';

const resolveImageUrl = (source) => {
  if (!source) return null;
  const sanitized = String(source).replace(/\\/g, '/').replace(/\s/g, '%20');
  if (/^https?:\/\//i.test(sanitized)) return sanitized;
  const base = getApiBaseUrl();
  if (sanitized.startsWith('/')) return `${base}${sanitized}`;
  if (sanitized.startsWith('uploads/')) return `${base}/${sanitized}`;
  return `${base}/uploads/${sanitized}`;
};

const ItemsDetallePage = () => {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { notify } = useNotifications();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const { settings } = useStoreSettings();

  useEffect(() => {
    if (itemId) {
      fetchProduct();
      fetchRelatedProducts();
    }
  }, [itemId]);

  useEffect(() => {
    if (!product?.item) return;
    const v = product.item.variantes || [];
    if (!Array.isArray(v) || v.length === 0) {
      setSelectedVariantId(null);
      return;
    }
    setSelectedVariantId((prev) => {
      if (prev != null && v.some((x) => x.id === prev)) return prev;
      return null;
    });
  }, [product?.id]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await fetch(buildApiUrl(`/api/products/public/item/${itemId}`));
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Producto no encontrado');
        }
        throw new Error('Error al cargar producto');
      }
      const data = await response.json();
      setProduct(data.product);
    } catch (error) {
      logError('[ItemsDetallePage] Error fetching product:', error);
      notify({ message: error.message || 'Error al cargar producto', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedProducts = async () => {
    try {
      const response = await fetch(buildApiUrl('/api/products/public/items'));
      if (!response.ok) throw new Error('Error al cargar productos relacionados');
      const data = await response.json();
      const filtered = (data.products || [])
        .filter(p => p.referenceId !== parseInt(itemId))
        .slice(0, 8);
      setRelatedProducts(filtered);
    } catch (error) {
      logError('[ItemsDetallePage] Error fetching related products:', error);
    }
  };

  const handleAddToCart = useCallback(async () => {
    if (!product) return;

    const it = product.item || {};
    const vars = Array.isArray(it.variantes) ? it.variantes : [];
    const sel = vars.find((v) => v.id === selectedVariantId) || null;
    const hasVariants = vars.length > 0;

    const stk = sel
      ? (sel.stock_variante ?? 0)
      : (it.stockMadre ?? it.stockdisponible ?? it.stock ?? 0);
    if (stk <= 0) {
      notify({ message: 'Este producto no tiene stock disponible', type: 'warning' });
      return;
    }

    if (quantity > stk) {
      notify({ message: `Solo hay ${stk} unidades disponibles`, type: 'warning' });
      return;
    }

    const listBase = sel
      ? parseFloat(sel.precio_variante) || 0
      : parseFloat(product.price) || 0;
    const priced = computeItemUnitPrice({
      listPrice: listBase,
      itemOferta: it.oferta,
      variantOferta: sel?.oferta_variante ?? sel?.OfertaDeVariable,
      productDiscountPercent: product.discountPercent,
    });
    const linePrice = priced.finalPrice;

    const imgs = it.images || it.imagenes || [];
    const fallbackImg =
      imgs[0]?.image_url || imgs[0] || null;
    const imgForCart = sel?.imagen_variante || fallbackImg;

    setAddingToCart(true);
    try {
      const cartItem = {
        id: product.id,
        productId: product.id,
        name: product.name || it.nombre,
        price: linePrice,
        image: imgForCart,
        productType: 'item',
        type: 'item',
        referenceId: product.referenceId,
        quantity,
        itemVariantId: sel?.id,
        variantName: sel?.nombre_variante || '',
      };

      const success = await addToCart(cartItem);
      if (success) {
        notify({ message: `${quantity} producto(s) agregado(s) al carrito`, type: 'success' });
      }
    } catch (error) {
      logError('[ItemsDetallePage] Error adding to cart:', error);
      notify({ message: 'Error al agregar al carrito', type: 'error' });
    } finally {
      setAddingToCart(false);
    }
  }, [product, selectedVariantId, quantity, addToCart, notify]);

  const handleQuantityChange = useCallback(
    (delta) => {
      if (!product) return;
      const it = product.item || {};
      const vars = Array.isArray(it.variantes) ? it.variantes : [];
      const sel = vars.find((v) => v.id === selectedVariantId) || null;
      const stk = sel
        ? (sel.stock_variante ?? 0)
        : (it.stockMadre ?? it.stockdisponible ?? it.stock ?? 0);
      const newQuantity = Math.max(1, Math.min(stk, quantity + delta));
      setQuantity(newQuantity);
    },
    [product, selectedVariantId, quantity],
  );

  if (loading) {
    return (
      <div className="product-detail-page">
        <div className="product-loading">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Cargando producto...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail-page">
        <div className="product-error">
          <i className="fas fa-exclamation-triangle"></i>
          <h2>Producto no encontrado</h2>
          <p>El producto que buscas no existe o no está disponible.</p>
          <button onClick={() => navigate('/shop')} className="btn-back">
            Volver a la Tienda
          </button>
        </div>
      </div>
    );
  }

  const item = product.item || {};
  const variantes = Array.isArray(item.variantes) ? item.variantes : [];
  const selectedVariant = variantes.find((v) => v.id === selectedVariantId) || null;
  const images = item.images || item.imagenes || [];
  const attributes = item.attributes || [];
  const stock = selectedVariant
    ? (selectedVariant.stock_variante ?? 0)
    : (item.stockMadre ?? item.stockdisponible ?? item.stock ?? 0);
  const hasStock = stock > 0;
  const listPrice = selectedVariant
    ? parseFloat(selectedVariant.precio_variante) || 0
    : parseFloat(product.price) || 0;
  const priced = computeItemUnitPrice({
    listPrice,
    itemOferta: item.oferta,
    variantOferta: selectedVariant?.oferta_variante ?? selectedVariant?.OfertaDeVariable,
    productDiscountPercent: product.discountPercent,
  });
  const finalPrice = priced.finalPrice;
  const discountPercent = priced.offerPercent;
  const hasOffer = priced.hasOffer;

  const mainImageSrc = selectedVariant?.imagen_variante
    ? resolveImageUrl(selectedVariant.imagen_variante)
    : images.length > 0
      ? resolveImageUrl(images[selectedImageIndex]?.image_url || images[selectedImageIndex])
      : null;

  return (
    <div className="product-detail-page">
      <div className="product-detail-container">
        {/* Breadcrumb */}
        <nav className="product-breadcrumb">
          <button onClick={() => navigate('/shop')} className="breadcrumb-link">
            Tienda
          </button>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">{product.name || item.nombre}</span>
        </nav>

        {/* Main Product Section */}
        <div className="product-main-section">
          {/* Image Gallery */}
          <div className="product-images-section">
            {images.length > 1 && !selectedVariant?.imagen_variante && (
              <div className="product-thumbnails">
                {images.map((img, index) => (
                  <div
                    key={index}
                    className={`product-thumbnail ${selectedImageIndex === index ? 'active' : ''}`}
                    onClick={() => setSelectedImageIndex(index)}
                  >
                    <img
                      src={resolveImageUrl(img.image_url || img)}
                      alt={`${product.name || item.nombre} - Imagen ${index + 1}`}
                    />
                  </div>
                ))}
              </div>
            )}
            <div className="product-main-image">
              {mainImageSrc ? (
                <img
                  src={mainImageSrc}
                  alt={product.name || item.nombre}
                />
              ) : (
                <div className="product-image-placeholder">
                  <i className="fas fa-image"></i>
                  <p>Sin imagen</p>
                </div>
              )}
            </div>
          </div>

          {/* Product Info */}
          <div className="product-info-section">
            <div className="product-header">
              <h1 className="product-title">{product.name || item.nombre}</h1>
              {item.sku && (
                <p className="product-sku">SKU: {item.sku}</p>
              )}
            </div>

            {variantes.length > 0 && (
              <div className="product-variant-picker">
                <p className="product-variant-label">
                  Opcional: elegí una variante (si no elegís, se usa el producto base)
                </p>
                <div className="product-variant-options" role="list">
                  <button
                    type="button"
                    role="listitem"
                    className={`product-variant-chip ${selectedVariantId == null ? 'active' : ''}`}
                    disabled={(item.stockMadre ?? item.stock ?? 0) <= 0}
                    onClick={() => {
                      setSelectedVariantId(null);
                      setQuantity(1);
                      setSelectedImageIndex(0);
                    }}
                  >
                    Producto base
                    {(item.stockMadre ?? item.stock ?? 0) <= 0 ? ' — Sin stock' : ''}
                  </button>
                  {variantes.map((v) => {
                    const vStock = v.stock_variante ?? 0;
                    const disabled = vStock <= 0;
                    return (
                      <button
                        key={v.id}
                        type="button"
                        role="listitem"
                        className={`product-variant-chip ${selectedVariantId === v.id ? 'active' : ''}`}
                        disabled={disabled}
                        onClick={() => {
                          setSelectedVariantId(v.id);
                          setQuantity(1);
                          setSelectedImageIndex(0);
                        }}
                      >
                        {v.nombre_variante}
                        {(v.oferta_variante > 0 || v.OfertaDeVariable > 0) &&
                          ` (−${v.oferta_variante ?? v.OfertaDeVariable}%)`}
                        {disabled ? ' — Sin stock' : ''}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Price Section */}
            <div className="product-price-section">
              {hasOffer ? (
                <>
                  <div className="price-group">
                    <span className="price-old">{formatARS(listPrice)}</span>
                    <span className="price-new">{formatARS(finalPrice)}</span>
                    {discountPercent > 0 && (
                      <span className="discount-badge">{discountPercent}% OFF</span>
                    )}
                  </div>
                  <p className="price-installment">o en 3 cuotas de {formatARS(finalPrice / 3)}</p>
                </>
              ) : (
                <>
                  <span className="price-current">{formatARS(listPrice)}</span>
                  <p className="price-installment">o en 3 cuotas de {formatARS(listPrice / 3)}</p>
                </>
              )}
            </div>

            <ShippingNotice
              variant="product"
              freeShippingThreshold={settings.freeShippingThreshold}
            />

            {/* Stock Info */}
            <div className="product-stock-info">
              <span className={`stock-dot ${hasStock ? 'in-stock' : 'out-of-stock'}`}></span>
              <span className="stock-text">
                {hasStock ? `${stock} unidades disponibles` : 'Sin stock disponible'}
              </span>
            </div>

            {/* Purchase Section */}
            {hasStock && (
              <div className="product-purchase-section">
                <div className="quantity-selector">
                  <label>Cantidad:</label>
                  <div className="quantity-controls">
                    <button
                      className="qty-btn"
                      onClick={() => handleQuantityChange(-1)}
                      disabled={quantity <= 1}
                    >
                      <i className="fas fa-minus"></i>
                    </button>
                    <input
                      type="number"
                      min="1"
                      max={stock}
                      value={quantity}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 1;
                        setQuantity(Math.max(1, Math.min(stock, val)));
                      }}
                      className="qty-input"
                    />
                    <button
                      className="qty-btn"
                      onClick={() => handleQuantityChange(1)}
                      disabled={quantity >= stock}
                    >
                      <i className="fas fa-plus"></i>
                    </button>
                  </div>
                </div>
                <button
                  className={`btn-add-cart ${!hasStock ? 'disabled' : ''}`}
                  onClick={handleAddToCart}
                  disabled={!hasStock || addingToCart}
                >
                  {addingToCart ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Agregando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-shopping-cart"></i>
                      Agregar al Carrito
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Description Section */}
        {item.descripcioncompletaitem && (
          <div className="product-description-section">
            <h2 className="section-title">Descripción</h2>
            <div className="description-content">
              <p>{item.descripcioncompletaitem}</p>
            </div>
          </div>
        )}

        {/* Characteristics Section */}
        {attributes.length > 0 && (
          <div className="product-characteristics-section">
            <h2 className="section-title">Características del producto</h2>
            <div className="characteristics-grid">
              {attributes.map((attr, index) => (
                <div key={index} className="characteristic-item">
                  <i className="fas fa-check-circle"></i>
                  <span className="characteristic-text">
                    <strong>{attr.attribute_key}:</strong> {attr.attribute_value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recommended Products Carousel */}
        {relatedProducts.length > 0 && (
          <div className="product-recommended-section">
            <h2 className="recommended-title">Productos recomendados</h2>
            <div className="recommended-carousel">
              {relatedProducts.map((relatedProduct) => {
                const relatedImage = relatedProduct.item?.imagenes?.[0] || relatedProduct.item?.images?.[0]?.image_url || null;
                const relatedStockMadre = relatedProduct.item?.stockMadre ?? relatedProduct.item?.stock ?? 0;
                const relatedStockVar = relatedProduct.item?.stockVariantes ?? 0;
                const relatedStock = relatedStockMadre > 0 ? relatedStockMadre : relatedStockVar;
                const relatedPriced = computeItemUnitPrice({
                  listPrice: relatedProduct.price,
                  itemOferta: relatedProduct.item?.oferta,
                  productDiscountPercent: relatedProduct.discountPercent,
                });
                const relatedPrice = relatedPriced.finalPrice;

                return (
                  <div
                    key={relatedProduct.productId}
                    className="recommended-card"
                    onClick={() => navigate(`/shop/${relatedProduct.referenceId}`)}
                  >
                    <div className="recommended-image">
                      {relatedImage ? (
                        <img src={resolveImageUrl(relatedImage)} alt={relatedProduct.name} />
                      ) : (
                        <div className="recommended-placeholder">
                          <i className="fas fa-box"></i>
                        </div>
                      )}
                      {relatedStock <= 0 && (
                        <div className="recommended-out-of-stock">
                          <span>Sin Stock</span>
                        </div>
                      )}
                    </div>
                    <div className="recommended-info">
                      <h4 className="recommended-name">{relatedProduct.name || relatedProduct.item?.nombre}</h4>
                      <div className="recommended-price">{formatARS(relatedPrice)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ItemsDetallePage;
